import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {TablePerformanceSummary} from 'notebook/performance/tableLimitUtils';
import {GRAY_LIGHTER} from 'ui-library/Colors';
import {HoverStateProps} from 'ui-library/hooks/useHoverState';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import SortableHeaderCell from 'ui-library/simple-table/SortableHeaderCell';
import useSimpleTableSortState from 'ui-library/simple-table/useSimpleTableSortState';
import sortedBy from 'util/sortedBy';

interface TablePerformanceSummaryTableProps {
  tableSummaries: Array<TablePerformanceSummary>;
  focusedTableName: string | null;
  onHover: (category: string) => void;
  onLeave: () => void;
}

export function TablePerformanceSummaryTable({
  tableSummaries,
  focusedTableName,
  onHover,
  onLeave,
}: TablePerformanceSummaryTableProps): JSX.Element {
  const {sortState, getHeaderCellProps} = useSimpleTableSortState('notebook-performance-table');

  const sortedRows = sortState
    ? sortedBy(tableSummaries, [
        {
          accessor: (summary: TablePerformanceSummary) => {
            if (sortState.columnKey === 'name') {
              return summary.name;
            }
            return summary[sortState.columnKey as keyof TablePerformanceSummary];
          },
          reversed: sortState.sortDirection === 'arrow-down',
        },
      ])
    : tableSummaries;

  const getHoverProps = (rowIndex: number): HoverStateProps => {
    const tableName = sortedRows[rowIndex].name;
    return {
      onMouseEnter: () => {
        onHover(tableName);
      },
      onMouseMove: () => {
        onHover(tableName);
      },
      onMouseLeave: onLeave,
    };
  };

  /**
   * When a section of the bar in the SizeBreakdown.tsx is hovered, manually set the hovered background color in the table
   */
  const isRowHovered = (rowIndex: number): boolean => focusedTableName === sortedRows[rowIndex].name;

  return (
    <Table
      className={css(styles.summaryTable)}
      numRows={sortedRows.length}
      frozenHeader={true}
      overflowVisible={true}
    >
      <Column
        header={
          <SortableHeaderCell {...getHeaderCellProps('name')} className={css(styles.nameColumn)}>
            Table Name
          </SortableHeaderCell>
        }
        renderCell={(rowIndex) => {
          const {colorHex, name} = sortedRows[rowIndex];
          return (
            <Cell
              hoverProps={getHoverProps(rowIndex)}
              className={css(styles.nameColumn, isRowHovered(rowIndex) && styles.hoveredRow)}
            >
              <div className={css(styles.nameWithMarker)}>
                <div style={{backgroundColor: colorHex}} className={css(styles.coloredMarker)} />
                <div className={css(styles.nameText)}>{name}</div>
              </div>
            </Cell>
          );
        }}
      />
      <Column
        header={
          <SortableHeaderCell {...getHeaderCellProps('numRows')} className={css(styles.dataColumn)}>
            Rows
          </SortableHeaderCell>
        }
        renderCell={(rowIndex) => (
          <Cell
            hoverProps={getHoverProps(rowIndex)}
            className={css(
              styles.rightAlignedCell,
              styles.dataColumn,
              isRowHovered(rowIndex) && styles.hoveredRow
            )}
          >
            {sortedRows[rowIndex].numRows}
          </Cell>
        )}
      />
      <Column
        header={
          <SortableHeaderCell {...getHeaderCellProps('numColumns')} className={css(styles.dataColumn)}>
            Columns
          </SortableHeaderCell>
        }
        renderCell={(rowIndex) => (
          <Cell
            hoverProps={getHoverProps(rowIndex)}
            className={css(
              styles.rightAlignedCell,
              styles.dataColumn,
              isRowHovered(rowIndex) && styles.hoveredRow
            )}
          >
            {sortedRows[rowIndex].numColumns}
          </Cell>
        )}
      />
    </Table>
  );
}

const styles = StyleSheet.create({
  hoveredRow: {
    backgroundColor: GRAY_LIGHTER,
  },
  nameWithMarker: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  nameText: {
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'pre-wrap',
  },
  summaryTable: {
    maxHeight: '425px',
    overflowY: 'auto',
  },
  coloredMarker: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  rightAlignedCell: {
    textAlign: 'right',
  },
  nameColumn: {
    width: '345px',
  },
  dataColumn: {
    width: '100px',
  },
});
